<template>
    <rested-card v-if="(isSpaceArchived || isSpaceRested) && isDevelopment"></rested-card>
    <v-card color="transparent" v-else flat class="w-100 align-self-start">
        <div class="d-flex">
            <div>
                <v-card-title class="secondary--text">Tables</v-card-title>
            </div>
            <v-spacer></v-spacer>
            <div class="mt-4">
                <v-menu :close-on-content-click="false" :nudge-width="100" :max-width="650" offset-y left>
                    <template v-slot:activator="{ on: menu }">
                        <v-btn text color="secondary" v-on="{ ...menu }" class="mr-2">
                            <v-icon small left>mdi-book-open-variant</v-icon>
                            connection guide
                        </v-btn>
                    </template>
                    <v-card
                        style="overflow-y: hidden"
                        :loading="fetchingSnowflakeTokens || fetchingUserInfo || settingTableUserMode || modeSwitchPending"
                        :disabled="fetchingSnowflakeTokens || fetchingUserInfo || settingTableUserMode || modeSwitchPending">
                        <v-card-title><span class="secondary--text">Connection guide for external applications</span></v-card-title>
                        <v-card-text v-if="userInfo.sf_user_type !== tableUserModes.NONE" class="pb-0">
                            <div class="d-flex align-center mb-2">
                                <p class="caption mb-0">CONNECTION MODE</p>
                                <v-btn icon small class="ml-1" href="https://docs.nuvolos.com/concepts/data-integration#connection-modes" target="_blank">
                                    <v-icon small>mdi-help-circle-outline</v-icon>
                                </v-btn>
                            </div>
                            <table-user-mode-switch
                                class="connection-mode-cards"
                                @mode-switch-start="modeSwitchPending = true"
                                @mode-switch-end="modeSwitchPending = false"></table-user-mode-switch>
                        </v-card-text>
                        <v-card-text v-if="userInfo.sf_user_type === tableUserModes.NONE">
                            <v-alert type="error" text>
                                Your tables access is broken, please get a new invitation to this space and accept it again to reconcile it!
                            </v-alert>
                        </v-card-text>
                        <v-card-text v-else-if="userInfo.sf_user_type === tableUserModes.SERVICE">
                            <v-alert type="info" text class="mb-0">
                                You currently have
                                <b>service mode</b>
                                for
                                <b>tables</b>
                                , which only enables access from within Nuvolos. Consult the
                                <a href="https://docs.nuvolos.com/reference/data-storage/access-data-from-applications" target="_blank">
                                    internal application guides
                                </a>
                                on how to work with the SDW.
                            </v-alert>
                        </v-card-text>
                        <template v-else>
                            <v-card-text v-if="currentSpaceType !== spaceTypes.VENDOR_SPACE">
                                <div class="d-flex flex-column">
                                    <span class="subtitle-2 secondary--text">
                                        If you want to connect to the database with a 3rd party application, please consult Nuvolos documentation
                                        <a href="https://docs.nuvolos.com/reference/data-storage/access-data-from-applications" target="_blank">here</a>
                                        .
                                    </span>
                                    <span class="subtitle-2 secondary--text mt-4">In any case, you'll need the following credentials to connect:</span>
                                    <v-row class="mt-2">
                                        <v-col v-for="(item, index) in connectionGuideData" :key="index" cols="12" :sm="item.sm">
                                            <label class="text-capitalize">{{ item.item }}</label>
                                            <v-sheet color="grey lighten-4" rounded class="d-flex align-center justify-space-between pa-3">
                                                <span class="subtitle-2 mr-2">{{ item.data }}</span>
                                                <copy-to-clipboard
                                                    :textToCopy="item.data"
                                                    buttonClass="caption font-weight-bold"
                                                    buttonColor="primary"
                                                    buttonName="copy to clipboard"
                                                    :iconButton="true"></copy-to-clipboard>
                                            </v-sheet>
                                        </v-col>
                                    </v-row>

                                    <span class="subtitle-2 secondary--text mt-4">
                                        Once connected, execute the following SQL command to activate the schema corresponding to the currently selected state:
                                    </span>
                                    <v-row class="mt-4">
                                        <v-col cols="12">
                                            <label>SQL schema command</label>
                                            <v-sheet color="grey lighten-4" rounded class="d-flex align-center justify-space-between pa-3">
                                                <span class="subtitle-2 mr-2">{{ connectionQuery }}</span>
                                                <copy-to-clipboard
                                                    :textToCopy="connectionQuery"
                                                    buttonClass="caption font-weight-bold"
                                                    buttonColor="primary"
                                                    buttonName="copy to clipboard"
                                                    :iconButton="true"></copy-to-clipboard>
                                            </v-sheet>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-card-text>
                            <v-card-text v-else-if="currentSpaceType === spaceTypes.VENDOR_SPACE">
                                <v-alert type="warning" prominent text>
                                    You cannot access vendor datasets directly from 3rd party applications. To access data with an external application, stage
                                    the tables you want to work with and distribute them to a course/research space.
                                </v-alert>
                            </v-card-text>
                        </template>
                    </v-card>
                </v-menu>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn text color="secondary" v-on="on" class="mr-2" href="https://docs.nuvolos.com/reference/data-storage/upload-data" target="_blank">
                            <v-icon small left>mdi-book-open-variant</v-icon>
                            import data
                        </v-btn>
                    </template>
                    <span>Read the documentation on how to import and upload data to Nuvolos</span>
                </v-tooltip>
                <v-dialog v-model="deactivateDialog" width="500" persistent v-if="isSpaceAdmin && tablesStatus === tableStatusOptions.ACTIVATED_TABLES">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn v-bind="attrs" v-on="on">Deactivate tables</v-btn>
                    </template>
                    <v-card>
                        <v-card-title class="flex-nowrap align-start">
                            Deactivating tables
                            <v-spacer></v-spacer>
                            <v-btn @click="deactivateDialog = false" icon><v-icon>clear</v-icon></v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-alert text type="warning">
                                Deactivating tables
                                <b>deletes all table data</b>
                                from all instances of this space,
                                <b>including snapshots!</b>
                            </v-alert>
                            <v-alert text type="error" class="mb-0">
                                This operation is
                                <b>irreversible!</b>
                                <div>Please confirm to proceed.</div>
                            </v-alert>
                            <dashboard-error v-if="deactivateError" :msg="deactivateError"></dashboard-error>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>

                            <v-btn color="error" @click="deactivate()" :loading="deactivateLoading" v-if="!deactivateSuccess">Deactivate</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </div>
        </div>

        <v-card-text>
            <div v-if="activatingTables" class="mb-4">
                <div class="caption secondary--text mb-1">Activating tables…</div>
                <v-progress-linear indeterminate color="primary" height="4"></v-progress-linear>
            </div>
            <template v-if="orgTablesStatus === true && tablesStatus === tableStatusOptions.ACTIVATED_TABLES">
                <v-card class="overflow-hidden">
                    <v-tabs ref="tabs" v-model="tab">
                        <v-tab v-for="tab in tabs" :key="tab.tabName">
                            <v-icon class="mr-1" small>{{ tab.icon }}</v-icon>
                            {{ tab.tabName }}
                        </v-tab>
                    </v-tabs>
                </v-card>

                <v-tabs-items v-model="tab">
                    <v-tab-item v-for="tabItem in tabs" :key="tabItem.tabName">
                        <component :is="tabItem.component"></component>
                    </v-tab-item>
                </v-tabs-items>
            </template>
            <v-alert v-else-if="tablesStatus === tableStatusOptions.ACTIVATING_TABLES" type="info" prominent text>
                <div class="d-flex flex-column">
                    <span class="font-weight-bold">Tables are being activated</span>
                    <span>Table activation can take a few minutes.</span>
                    <div class="d-flex align-center mt-2">
                        <v-icon color="info" small>mdi-arrow-right</v-icon>
                        <span class="caption font-weight-bold info--text">
                            Upon completion of table activation, running applications need to be restarted in order to integrate the tables feature.
                        </span>
                    </div>
                </div>
            </v-alert>
            <v-alert v-else-if="tablesStatus === tableStatusOptions.INACTIVE_TABLES" type="info" prominent text>
                <div class="d-flex justify-space-between align-center w-100">
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Tables not activated</span>
                        <div>
                            The tables feature hasn't been activated for this
                            <span v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE">course</span>
                            <span v-else-if="currentSpaceType === spaceTypes.RESEARCH_SPACE">research project</span>
                            <span v-else-if="currentSpaceType === spaceTypes.VENDOR_SPACE">dataset</span>
                            yet.
                            <span v-if="!isSpaceAdmin">Contact a space administrator to request the activation of tables.</span>
                            <span v-else-if="!tablesCanBeEnabled">Contact {{ whoEnablesFeature[tablesDisabledReason] }} to enable it.</span>
                        </div>
                    </div>
                    <v-btn
                        v-if="isSpaceAdmin && tablesCanBeEnabled"
                        small
                        color="info"
                        outlined
                        :loading="activatingTables"
                        :disabled="activatingTables"
                        class="ml-4"
                        @click="activateTable">
                        activate tables
                    </v-btn>
                </div>
            </v-alert>
            <v-card v-else-if="!tablesFetching && orgTablesStatus === false && !isTrialSpace" flat>
                <v-card-text>
                    <v-alert type="info" prominent text>
                        <div class="d-flex flex-column">
                            <span class="font-weight-bold">Tables not activated</span>
                            The current plan does not allow for the usage of Tables in Nuvolos. Please reach out to your organization administrator if you
                            require this feature.
                        </div>
                    </v-alert>
                </v-card-text>
            </v-card>
            <v-card v-else-if="!tablesFetching" flat>
                <v-card-text>
                    <v-alert type="info" prominent text>
                        <div class="d-flex flex-column">Data on tables status is currently not avaiable.</div>
                    </v-alert>
                </v-card-text>
            </v-card>
            <v-card flat v-if="isTrialSpace">
                <v-card-text>
                    <v-alert type="info" prominent text>
                        <div class="d-flex flex-column">
                            <span class="font-weight-bold">You discovered a premium feature!</span>
                            Subscribing to Nuvolos will unlock it.
                        </div>
                    </v-alert>
                </v-card-text>
            </v-card>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { queryData } from '@/mixins/query'
import eventBus from '@/components/EventBus'
import { fetchTask } from '@/apis'
import { on, onceOrTimeout, PushEventKind } from '@/push'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'
import * as spaceErrors from '@/modules/space/errors'
import * as tablesErrors from '@/modules/snapshot/errors/tables'

const { getBackendErrorMessage } = createBackendErrorHandler(lifecycleErrors, permissionsErrors, snapshotErrors, spaceErrors, tablesErrors)

const DashboardError = () => import('@/components/DashboardError.vue')
const CodeHighlight = () => import('@/components/CodeHighlight')
const TheSnapshotQueryBuilder = () => import('../components/TheSnapshotQueryBuilder')
const TheSnapshotTableList = () => import('../components/TheSnapshotTableList')
const TheSnapshotTableQueries = () => import('../components/TheSnapshotTableQueries')
const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const TableUserModeSwitch = () => import('@/modules/user/components/TableUserModeSwitch.vue')
const RestedCard = () => import('@/modules/snapshot/components/RestedCard.vue')

const IN_FLIGHT_QUERY_STATUSES = new Set(['RUNNING', 'COPYING'])

export default {
    name: 'SnapshotTable',
    components: {
        TheSnapshotQueryBuilder,
        TheSnapshotTableList,
        TheSnapshotTableQueries,
        CopyToClipboard,
        CodeHighlight,
        DashboardError,
        TableUserModeSwitch,
        RestedCard
    },
    mixins: [enumsData, queryData],
    data() {
        return {
            tab: 0,
            tabValues: {
                TABLES: 0,
                QUERYBUILDER: 1,
                QUERIES: 2
            },
            queryUpdater: null,
            tableStatusOptions: {
                ACTIVATED_TABLES: 'ACTIVE',
                ACTIVATING_TABLES: 'ACTIVATING',
                INACTIVE_TABLES: 'INACTIVE',
                EMPTY_TABLES: 'EMPTY'
            },
            tablePollingInterval: null,
            tablesCanBeEnabled: false,
            tablesDisabledReason: '',
            whoEnablesFeature: {
                TURNED_OFF_RESOURCE_POOL: `this space's resource pool manager`,
                TURNED_OFF_ORG: "this space's organization manager",
                DISABLED: 'our support'
            },
            modeSwitchPending: false,
            deactivateDialog: false,
            deactivateError: '',
            deactivateSuccess: false,
            deactivateLoading: false,
            activatingTables: false,
            // tkid -> unsubscribe fn for in-flight query task_finished listeners
            queryTaskUnsubs: null
        }
    },
    created() {
        this.queryTaskUnsubs = new Map()
        eventBus.$on('querySubmitSuccess', evt => {
            this.tab = this.tabValues.QUERIES
        })
    },
    computed: {
        ...mapGetters('spaceStore', ['currentSpaceType', 'isSpaceAdmin', 'isTrialSpace', 'isSpaceRested', 'isSpaceArchived']),
        ...mapState('snapshotStore', ['tablesPath', 'tablesFetching', 'snapshotQueries']),
        ...mapState('userStore', ['snowflakeTokens', 'fetchingSnowflakeTokens', 'settingTableUserMode']),
        ...mapGetters('orgStore', ['orgTablesStatus']),
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapState(['userInfo', 'fetchingUserInfo']),
        connectionQuery() {
            const db = this.tablesPath.db
            const schema = this.tablesPath.schema
            return 'USE SCHEMA "' + db + '"' + '."' + schema + '"'
        },
        spaceTableData() {
            if (this.orgTablesStatus === true && this.spacesWithPrimarySnapshots) {
                const currentSpaceData = this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.$route.params.sid.toString())
                if (currentSpaceData) {
                    return currentSpaceData
                }
            }
            return null
        },
        tablesStatus() {
            if (this.spaceTableData) {
                if (this.spaceTableData.space_lock_mode_name === this.$data.tableStatusOptions.ACTIVATING_TABLES && !this.tablesFetching) {
                    return this.$data.tableStatusOptions.ACTIVATING_TABLES
                } else if (this.spaceTableData.has_tables === null && this.isDevelopment && !this.tablesFetching) {
                    return this.$data.tableStatusOptions.INACTIVE_TABLES
                } else if (this.spaceTableData.has_tables) {
                    return this.$data.tableStatusOptions.ACTIVATED_TABLES
                } else if (
                    this.spaceTableData.has_tables === null &&
                    this.spaceTableData.space_lock_mode_name !== this.$data.tableStatusOptions.ACTIVATING_TABLES &&
                    !this.isDevelopment &&
                    !this.tablesFetching
                ) {
                    return this.$data.tableStatusOptions.EMPTY_TABLES
                }
            }
            return null
        },
        connectionGuideData() {
            const username = this.userInfo.username
            const password = this.snowflakeTokens.access_token
            const schema = this.tablesPath.schema
            const database = this.tablesPath.db
            const hostname = 'alphacruncher.eu-central-1.snowflakecomputing.com'
            const fullData = [
                { item: 'username', data: username, sm: 6 },
                { item: 'password', data: password, sm: 6 },
                { item: 'schema', data: schema, sm: 6 },
                { item: 'database', data: database, sm: 6 },
                { item: 'hostname', data: hostname, sm: 12 }
            ]
            return fullData
        },
        tabs() {
            return [
                { tabName: 'Tables', icon: 'mdi-table-large', component: 'TheSnapshotTableList' },
                { tabName: 'Query builder', icon: 'filter_alt', component: 'TheSnapshotQueryBuilder' },
                { tabName: 'Queries', icon: 'query_stats', component: 'TheSnapshotTableQueries' }
            ]
        }
    },
    methods: {
        async deactivate() {
            try {
                this.deactivateLoading = true
                const { data } = await this.$axios.delete(`spaces/${this.$route.params.sid}/delete_tables_async`)
                const taskResult = await fetchTask(data.tkid)
                if (taskResult instanceof Error) {
                    this.deactivateError = taskResult.message
                } else {
                    this.deactivateSuccess = true
                    await new Promise(resolve => setTimeout(() => resolve(), 2000))
                    this.deactivateDialog = false
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                    this.resetIntervals()
                }
            } catch (error) {
                this.deactivateError = 'Deactivate failed because of network error.'
            } finally {
                this.deactivateLoading = false
            }
        },
        clearTableStatusInterval() {
            clearInterval(this.tablePollingInterval)
            this.tablePollingInterval = null
        },
        clearQueriesInterval() {
            clearInterval(this.queryUpdater)
            this.queryUpdater = null
        },
        clearQueryTaskSubscriptions() {
            if (!this.queryTaskUnsubs) return
            for (const unsub of this.queryTaskUnsubs.values()) {
                try {
                    unsub()
                } catch (err) {
                    // ignore
                }
            }
            this.queryTaskUnsubs.clear()
        },
        syncQueryTaskSubscriptions() {
            if (!this.queryTaskUnsubs) this.queryTaskUnsubs = new Map()
            const queries = this.snapshotQueries || []
            const wanted = new Set()
            for (const row of queries) {
                if (!row || row.tkid == null) continue
                if (!IN_FLIGHT_QUERY_STATUSES.has(row.status_name)) continue
                const key = String(row.tkid)
                wanted.add(key)
                if (this.queryTaskUnsubs.has(key)) continue
                const unsub = on(PushEventKind.TASK_FINISHED, data => {
                    if (String(data.tkid) !== key) return
                    unsub()
                    this.queryTaskUnsubs.delete(key)
                    if (this.$route.params.snid) {
                        this.$store.dispatch('snapshotStore/fetchQueries', this.$route.params.snid)
                    }
                })
                this.queryTaskUnsubs.set(key, unsub)
            }
            for (const key of [...this.queryTaskUnsubs.keys()]) {
                if (!wanted.has(key)) {
                    const unsub = this.queryTaskUnsubs.get(key)
                    if (typeof unsub === 'function') unsub()
                    this.queryTaskUnsubs.delete(key)
                }
            }
        },
        setQueriesInterval() {
            // DISABLED standing poll — in-flight rows refresh via task_finished;
            // full list on activated() + when subscriptions fire. Restore if needed:
            // this.$data.queryUpdater = setInterval(() => {
            //     if (this.$auth.isAuthenticated && this.tablesStatus === this.tableStatusOptions.ACTIVATED_TABLES) {
            //         this.$store.dispatch('snapshotStore/fetchQueries', this.$route.params.snid)
            //     }
            // }, 5000)
            this.clearQueriesInterval()
        },
        async activateTable() {
            if (this.activatingTables) return
            this.activatingTables = true
            this.$store.dispatch('showSnackBar', {
                snackBarText: 'Activating tables...',
                snackBarTimeout: 10000,
                snackBarIcon: 'info'
            })
            try {
                const response = await this.$axios.get(`/spaces/${this.$route.params.sid}/activate_async`)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Table activation has started...',
                    snackBarTimeout: 10000,
                    snackBarIcon: 'info'
                })
                this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                // Push-primary: wait for task_finished (long ceiling). No standing 10s poll.
                // this.setTableStatusPollingInterval()
                const tkid = response && response.data && response.data.tkid
                if (tkid == null) {
                    // No tkid — single reconcile only.
                    this.fetchTableStatus()
                    return
                }
                const hit = await onceOrTimeout(PushEventKind.TASK_FINISHED, d => String(d.tkid) === String(tkid), 300000)
                this.clearTableStatusInterval()
                if (!hit) {
                    // Push missed — one-shot reconcile (old poll path as fallback).
                    this.fetchTableStatus()
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                    return
                }
                this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Tables activated',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Table activation failed. Please try again.')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 10000,
                        snackBarIcon: 'error'
                    })
                }
                this.fetchTableStatus()
            } finally {
                this.activatingTables = false
            }
        },
        fetchTableStatus() {
            // One-shot status check (no standing poll). If still ACTIVATING, org spaces
            // refresh is enough for the banner; completion arrives via task_finished when
            // this session started activate, or on next navigation/push reconnect.
            this.$axios.post('/spaces/find', { oid: this.$route.params.oid, sid: this.$route.params.sid }).then(response => {
                if (response.data && response.data[0].has_tables) {
                    this.clearTableStatusInterval()
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                } else if (response.data && response.data[0].lock_mode_name === this.tableStatusOptions.ACTIVATING_TABLES) {
                    // Was: this.setTableStatusPollingInterval()
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                }
            })
        },
        checkRouteQuery() {
            if (this.$route.query.tab && this.$route.query.tab === this.tableViewTabNames.TABLES) {
                this.tab = this.tabValues.TABLES
            } else if (this.$route.query.tab && this.$route.query.tab === this.tableViewTabNames.QUERIES) {
                this.tab = this.tabValues.QUERIES
            } else if (this.$route.query.tab && this.$route.query.tab === this.tableViewTabNames.QUERYBUILDER) {
                this.tab = this.tabValues.QUERYBUILDER
            } else if (this.$route.query.tab !== this.tableViewTabNames.TABLES) {
                this.$router.push({ query: { tab: this.tableViewTabNames.TABLES } })
            }
        },
        setRouteQuery(nextTab) {
            if (nextTab === this.tabValues.QUERIES && this.$route.query.tab !== this.tableViewTabNames.QUERIES) {
                this.$router.push({ query: { tab: this.tableViewTabNames.QUERIES } })
            } else if (nextTab === this.tabValues.QUERYBUILDER && this.$route.query.tab !== this.tableViewTabNames.QUERYBUILDER) {
                this.$router.push({ query: { tab: this.tableViewTabNames.QUERYBUILDER } })
            } else if (nextTab === this.tabValues.TABLES && this.$route.query.tab !== this.tableViewTabNames.TABLES) {
                this.$router.push({ query: { tab: this.tableViewTabNames.TABLES } })
            } else if (!this.$route.query.tab) {
                this.$router.push({ query: { tab: this.tableViewTabNames.TABLES } })
            }
            // Keep query task_finished subs across tabs on this page (no poll fallback).
            this.syncQueryTaskSubscriptions()
        },
        setTableStatusPollingInterval() {
            // DISABLED — activateTable waits on task_finished. Restore if needed:
            // this.clearTableStatusInterval()
            // this.tablePollingInterval = setInterval(() => {
            //     this.fetchTableStatus()
            // }, 10000)
            this.clearTableStatusInterval()
        },
        async fetchTablesFeatureStatus() {
            try {
                const { data } = await this.$axios.get(`/spaces/${this.$route.params.sid}/can_enable_tables`)
                this.tablesCanBeEnabled = data.can_enable_tables
                this.tablesDisabledReason = data.reason
            } catch (error) {
                console.log('error:', error)
                const snackBarText = getBackendErrorMessage(error, `Could not retrieve table activation status.`)
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 2000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        resetIntervals() {
            this.clearQueriesInterval()
            this.clearTableStatusInterval()
            this.clearQueryTaskSubscriptions()
        }
    },
    watch: {
        tab: function (to) {
            setTimeout(() => {
                if (this.$refs.tabs) {
                    this.$refs.tabs.onResize()
                }
            }, 300)
            this.setRouteQuery(to)
        },
        snapshotQueries() {
            // Without the 5s poll, keep task_finished subs for any in-flight query
            // even if the user is on another tab of this page.
            this.syncQueryTaskSubscriptions()
        },
        $route: {
            handler(to, from) {
                if (
                    to.name === 'snapshot-tables' &&
                    to.query &&
                    to.query.tab === this.tableViewTabNames.QUERYBUILDER &&
                    this.tab !== this.tabValues.QUERYBUILDER
                ) {
                    this.tab = this.tabValues.QUERYBUILDER
                }
                if (
                    from &&
                    from.params.snid &&
                    (!to || !to.params.snid || (to && to.params.snid && from.params.snid.toString() !== to.params.snid.toString()))
                ) {
                    this.tab = this.tabValues.TABLES
                }
            }
        }
    },
    mounted() {
        this.$store.dispatch('userStore/fetchSnowflakeTokens')
        this.fetchTablesFeatureStatus()
    },
    beforeDestroy() {
        this.resetIntervals()
    },
    activated() {
        this.fetchTableStatus()
        this.$store.dispatch('snapshotStore/setTablesPath', this.$route.params.snid)
        this.$store.dispatch('snapshotStore/fetchQueries', this.$route.params.snid).then(() => {
            this.syncQueryTaskSubscriptions()
        })
        this.setQueriesInterval() // no-op placeholder; poll commented inside
        this.checkRouteQuery()
    },
    deactivated() {
        this.clearQueryTaskSubscriptions()
    }
}
</script>
